import Card from "@rio-cloud/rio-uikit/Card";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";

interface TopConcessionariasChartSkeletonProps {
  bars?: number;
  labelLines?: number;
  yTicks?: number;
}

const TITLE_LINE_HEIGHT = 29;
const AXIS_TICK_HEIGHT = 12;
const LINE_HEIGHT = 14;
const X_AXIS_EXTRA_HEIGHT = 16;
const BARS_AREA_HEIGHT = 300;
const Y_AXIS_WIDTH = 80;
const BAR_SIZE = 90;
const BAR_RADIUS = 4;

const MIN_BAR_PERCENTAGE = 18;
const MAX_BAR_PERCENTAGE = 96;

const LABEL_WIDTHS = [70, 50, 60];

export default function TopConcessionariasChartSkeleton({
  bars = 10,
  labelLines = 2,
  yTicks = 6,
}: Readonly<TopConcessionariasChartSkeletonProps>) {
  const xAxisHeight = labelLines * LINE_HEIGHT + X_AXIS_EXTRA_HEIGHT;
  const chartTotalHeight = BARS_AREA_HEIGHT + xAxisHeight;
  const lastIndex = Math.max(1, bars - 1);

  const barColumns = Array.from({ length: bars }, (_, index) => {
    const percentage = MIN_BAR_PERCENTAGE + ((MAX_BAR_PERCENTAGE - MIN_BAR_PERCENTAGE) * index) / lastIndex;
    return {
      key: `top-concessionarias-skeleton-bar-${index}`,
      height: Math.round((percentage / 100) * BARS_AREA_HEIGHT),
    };
  });

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm display-flex flex-column overflow-x-auto">
      <div className="padding-bottom-20">
        <span className="text-size-20 text-bold">
          <ContentLoader className="display-block" height={TITLE_LINE_HEIGHT} width={300} />
        </span>
      </div>

      <div className="width-1000 width-100pct-md" style={{ height: chartTotalHeight }}>
        <div className="display-flex flex-column" style={{ height: chartTotalHeight }}>
          <div className="display-flex" style={{ height: BARS_AREA_HEIGHT }}>
            <div
              className="display-flex flex-column justify-content-between"
              style={{ width: Y_AXIS_WIDTH, alignItems: "flex-end", paddingRight: 5, flexShrink: 0 }}
            >
              {Array.from({ length: yTicks }, (_, index) => (
                <ContentLoader key={index} height={AXIS_TICK_HEIGHT} width={60} />
              ))}
            </div>

            <div className="display-flex" style={{ flex: 1, justifyContent: "space-around" }}>
              {barColumns.map((barColumn) => (
                <div
                  key={barColumn.key}
                  className="display-flex"
                  style={{
                    width: BAR_SIZE,
                    height: BARS_AREA_HEIGHT,
                    alignItems: "flex-end",
                    flexShrink: 0,
                  }}
                >
                  <ContentLoader height={barColumn.height} width={BAR_SIZE} rx={BAR_RADIUS} ry={BAR_RADIUS} />
                </div>
              ))}
            </div>
          </div>

          <div
            className="display-flex"
            style={{
              height: xAxisHeight,
              paddingLeft: Y_AXIS_WIDTH,
              paddingTop: 4,
              justifyContent: "space-around",
            }}
          >
            {barColumns.map((barColumn) => (
              <div
                key={`${barColumn.key}-label`}
                className="display-flex flex-column align-items-center gap-2"
                style={{ width: BAR_SIZE, flexShrink: 0 }}
              >
                {Array.from({ length: labelLines }, (_, lineIndex) => (
                  <ContentLoader
                    key={lineIndex}
                    height={AXIS_TICK_HEIGHT}
                    width={LABEL_WIDTHS[lineIndex % LABEL_WIDTHS.length]}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
